import { MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const SKYLINE =
    "https://images.unsplash.com/photo-1774666011220-e1cc2b19ed79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhaG1lZGFiYWQlMjBjaXR5JTIwc2t5bGluZXxlbnwwfHx8fDE3ODIxNTU3MzN8MA&ixlib=rb-4.1.0&q=85&w=1600";

const Coverage = () => {
    const { lang, t } = useLanguage();
    const gu = lang === "gu" ? "font-gujarati" : "";
    const cities = t("coverage.cities") || [];

    return (
        <section
            id="coverage"
            data-testid="coverage-section"
            className="py-24 md:py-32 bg-white relative"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <span
                            className={`inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0B308A] ${gu}`}
                        >
                            {t("coverage.overline")}
                        </span>
                        <h2
                            className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 leading-[1.1] ${gu}`}
                        >
                            {t("coverage.title")}
                        </h2>
                    </div>
                    <p
                        className={`lg:col-span-5 text-slate-600 leading-relaxed text-[16px] ${gu}`}
                    >
                        {t("coverage.subtitle")}
                    </p>
                </div>

                {/* Skyline banner */}
                <div className="mt-14 relative overflow-hidden rounded-sm">
                    <img
                        src={SKYLINE}
                        alt="Gujarat skyline"
                        loading="lazy"
                        className="w-full h-[280px] sm:h-[340px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0B308A]/85 via-[#0B308A]/55 to-transparent" />
                    <div className="absolute inset-0 flex items-center px-8 sm:px-12">
                        <div className="text-white max-w-xl">
                            <div className="inline-flex items-center gap-2 text-[#FACC15] text-[12px] uppercase tracking-[0.22em] font-semibold">
                                <MapPin className="w-4 h-4" /> Gujarat
                            </div>
                            <div
                                className={`mt-4 font-display text-2xl sm:text-3xl font-bold ${gu}`}
                            >
                                {t("coverage.note")}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cities marquee */}
                <div
                    data-testid="cities-marquee"
                    className="mt-10 overflow-hidden border-y border-slate-100 py-5 [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]"
                >
                    <div className="flex gap-3 whitespace-nowrap sg-marquee">
                        {[...cities, ...cities].map((c, i) => (
                            <span
                                key={i}
                                data-testid={`city-${i}`}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 bg-[#F8FAFC] hover:bg-[#EFF6FF] border border-slate-100 text-slate-700 text-[14px] font-medium rounded-sm ${gu}`}
                            >
                                <MapPin className="w-3.5 h-3.5 text-[#0B308A]" />
                                {c}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Static grid for accessibility/SEO */}
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
                    {cities.slice(0, 20).map((c, i) => (
                        <div
                            key={`grid-${i}`}
                            className={`text-[13.5px] text-slate-700 px-3 py-2 rounded-sm hover:bg-[#EFF6FF] hover:text-[#0B308A] transition-colors ${gu}`}
                        >
                            • {c}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Coverage;
